import { useCallback } from 'react'

import { formatDurationHuman } from '@/utils'
import { useAtomCallback } from 'jotai/utils'

import { assert } from '@/utils/assert'

import { formatTimeOfDay } from '~shared/timeutils'
import { RunnerState } from '~shared/types'
import { CueRunState } from '~shared/utils'

import { runnerAtom, runnerStateAtom } from '@/store/runner.store'
import { getTimestampByCueIdAtom } from '@/store/timestamps.store'

import { getCue } from '../../data/cues.store'
import { rundownPageStore } from '../../rundown-page.store'
import { getSortedCues } from '../cue.derived'

import { bindFixActions, computeTimingFixes } from './timingFixes'
import type {
  TimingFix,
  TimingFixAction,
  TimingFixParams,
} from './timingFixTypes'

/**
 * Build the gap/overlap "Fix" menu planner for a cue.
 *
 * @remarks
 *   The pure decisions and labels come from `computeTimingFixes`. This hook is
 *   the adapter that gathers the store inputs and binds each plan's action to
 *   `runFixAction` for the dropdown. Everything is read at call time (when the
 *   menu opens).
 */
export function useTimingFixPlanner(
  cueId: string,
  runFixAction: (action: TimingFixAction) => void,
): (params: TimingFixParams) => TimingFix[] {
  assert(
    rundownPageStore.rundown.id,
    'useTimingFixPlanner: rundown is not initialized',
  )

  // Read all imperatively
  const readLive = useAtomCallback(
    useCallback(
      (get) => ({
        runner: get(runnerAtom),
        runnerState: get(runnerStateAtom),
        getTimestamp: get(getTimestampByCueIdAtom),
      }),
      [],
    ),
  )

  return useCallback(
    ({ type }: TimingFixParams): TimingFix[] => {
      // Read from proxy: `cueOrder` is mutable on the proxy, which
      // `computeTimingFixes` expects.
      const rundown = rundownPageStore.rundown
      const cue = getCue(cueId)
      if (!cue?.startTime) return []

      const { runner, runnerState, getTimestamp } = readLive()

      const plans = computeTimingFixes({
        ctx: {
          cues: getSortedCues(),
          cueOrder: rundown.cues,
          rundownStartTime: rundown.startTime,
          timezone: rundown.timezone ?? undefined,
          runner,
        },
        anchorCueId: cueId,
        type,
        formatTime: (d: Date) =>
          formatTimeOfDay(d, {
            timezone: rundown.timezone ?? undefined,
            format: rundown.settings?.todDisplayFormat,
            seconds: 'nonzero',
          }),
        formatDuration: (ms: number) => formatDurationHuman(ms, { prefix: '' }),
        getCueTitle: (id: string) => getCue(id)?.title ?? '',
        // Live/ended: the runner drives the timeline, so fixes that write an
        // already-played cue can't take effect (see computeTimingFixes).
        // PRESHOW has no runner-locked cues, so leave every fix enabled there.
        isRunning: runnerState !== RunnerState.PRESHOW,
        isCuePlayed: (id: string) => {
          const state = getTimestamp(id)?.state
          return (
            state === CueRunState.CUE_PAST || state === CueRunState.CUE_ACTIVE
          )
        },
      })

      return bindFixActions(plans, runFixAction)
    },
    [cueId, readLive, runFixAction],
  )
}
